import { env } from "../env";

/**
 * Проверяет, является ли IP-адрес IPv6
 * @param ip IP-адрес для проверки
 * @returns true, если это IPv6
 */
const isIPv6 = (ip: string): boolean => {
  return ip.includes(":");
};

/**
 * Проверяет, является ли IP-адрес IPv4
 * @param ip IP-адрес для проверки
 * @returns true, если это IPv4
 */
const isIPv4 = (ip: string): boolean => {
  return ip.includes(".") && !ip.includes(":");
};

/**
 * Конвертирует IPv4 адрес в число
 * @param ip IPv4 адрес в строковом формате
 * @returns число или null, если IP неверный
 */
const ipv4ToNumber = (ip: string): number | null => {
  try {
    const parts = ip.split(".").map((part) => parseInt(part, 10));

    if (
      parts.length !== 4 ||
      parts.some((part) => Number.isNaN(part) || part < 0 || part > 255)
    ) {
      return null;
    }

    // Используем умножение вместо побитовых операций
    return parts[0] * 16777216 + parts[1] * 65536 + parts[2] * 256 + parts[3];
  } catch (error) {
    return null;
  }
};

/**
 * Нормализует IPv6 адрес, раскрывая сокращения
 * @param ip IPv6 адрес
 * @returns нормализованный IPv6 адрес или null
 */
const normalizeIPv6 = (ip: string): string | null => {
  try {
    // Убираем префикс сети если есть
    const [address] = ip.split("/");

    // Обрабатываем случай с двойным двоеточием (::)
    if (address.includes("::")) {
      const [left, right] = address.split("::");
      const leftGroups = left ? left.split(":") : [];
      const rightGroups = right ? right.split(":") : [];

      // Вычисляем количество нулевых групп
      const zeroGroups = 8 - leftGroups.length - rightGroups.length;

      // Создаем массив с нулевыми группами
      const zeroArray = Array(zeroGroups).fill("0000");

      // Объединяем все группы
      const allGroups = [...leftGroups, ...zeroArray, ...rightGroups];

      // Дополняем нулями до 8 групп
      while (allGroups.length < 8) {
        allGroups.push("0000");
      }

      return allGroups.slice(0, 8).join(":");
    }

    // Простой случай без сокращений
    const groups = address.split(":");

    // Дополняем нулями до 8 групп
    while (groups.length < 8) {
      groups.push("0000");
    }

    return groups.slice(0, 8).join(":");
  } catch (error) {
    return null;
  }
};

/**
 * Конвертирует IPv6 адрес в BigInt
 * @param ip IPv6 адрес в строковом формате
 * @returns BigInt или null, если IP неверный
 */
const ipv6ToBigInt = (ip: string): bigint | null => {
  try {
    // Нормализуем IPv6 адрес (раскрываем сокращения)
    const normalized = normalizeIPv6(ip);
    if (!normalized) {
      return null;
    }

    // Разбиваем на 8 групп по 16 бит
    const groups = normalized.split(":").map((group) => parseInt(group, 16));

    if (
      groups.length !== 8 ||
      groups.some((group) => Number.isNaN(group) || group < 0 || group > 0xffff)
    ) {
      return null;
    }

    // Конвертируем в BigInt
    let result = BigInt(0);
    for (let i = 0; i < 8; i += 1) {
      result = result * BigInt(65536) + BigInt(groups[i]);
    }

    return result;
  } catch (error) {
    return null;
  }
};

/**
 * Проверяет, входит ли IP-адрес в указанную подсеть (IPv4)
 * @param ip IPv4 адрес для проверки
 * @param cidr CIDR-нотация (например, "10.100.0.0/16")
 * @returns true, если IP входит в подсеть
 */
const isIPv4InSubnet = (ip: string, cidr: string): boolean => {
  try {
    const [subnet, prefixLength] = cidr.split("/");
    const mask = parseInt(prefixLength, 10);

    if (Number.isNaN(mask) || mask < 0 || mask > 32) {
      return false;
    }

    // Конвертируем IP-адреса в числа
    const ipNum = ipv4ToNumber(ip);
    const subnetNum = ipv4ToNumber(subnet);

    if (ipNum === null || subnetNum === null) {
      return false;
    }

    // Создаем маску подсети без побитовых операций
    const maskNum = mask === 32 ? 0xffffffff : 2 ** 32 - 2 ** (32 - mask);

    // Проверяем, входит ли IP в подсеть (используем побитовые операции только здесь)
    // eslint-disable-next-line no-bitwise
    return (ipNum & maskNum) === (subnetNum & maskNum);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Ошибка при проверке IPv4 ${ip} в подсети ${cidr}:`, error);
    return false;
  }
};

/**
 * Проверяет, входит ли IP-адрес в указанную подсеть (IPv6)
 * @param ip IPv6 адрес для проверки
 * @param cidr CIDR-нотация (например, "2001:db8::/64")
 * @returns true, если IP входит в подсеть
 */
const isIPv6InSubnet = (ip: string, cidr: string): boolean => {
  try {
    const [subnet, prefixLength] = cidr.split("/");
    const mask = parseInt(prefixLength, 10);

    if (Number.isNaN(mask) || mask < 0 || mask > 128) {
      return false;
    }

    // Конвертируем IPv6 адреса в BigInt
    const ipNum = ipv6ToBigInt(ip);
    const subnetNum = ipv6ToBigInt(subnet);

    if (ipNum === null || subnetNum === null) {
      return false;
    }

    // Создаем маску подсети для IPv6 без побитовых операций
    const fullMask = BigInt("0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF");
    const maskNum =
      mask === 128
        ? fullMask
        : fullMask - BigInt(1) * BigInt(2) ** BigInt(128 - mask);

    // Проверяем, входит ли IP в подсеть (используем побитовые операции только здесь)
    // eslint-disable-next-line no-bitwise
    return (ipNum & maskNum) === (subnetNum & maskNum);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Ошибка при проверке IPv6 ${ip} в подсети ${cidr}:`, error);
    return false;
  }
};

/**
 * Проверяет, входит ли IP-адрес в указанную подсеть
 * @param ip IP-адрес для проверки
 * @param cidr CIDR-нотация (например, "10.100.0.0/16" или "2001:db8::/64")
 * @returns true, если IP входит в подсеть
 */
export const isIPInSubnet = (ip: string, cidr: string): boolean => {
  if (isIPv6(ip)) {
    return isIPv6InSubnet(ip, cidr);
  }
  if (isIPv4(ip)) {
    return isIPv4InSubnet(ip, cidr);
  }
  return false;
};

/**
 * Проверяет, разрешен ли IP-адрес (включая проверку подсетей)
 * @param ip IP-адрес для проверки
 * @param allowedIPs список разрешенных IP-адресов и подсетей
 * @returns true, если IP разрешен
 */
export const isIPAllowed = (ip: string, allowedIPs: string[]): boolean => {
  return allowedIPs.some((allowed) => {
    // Если это CIDR-нотация (содержит '/')
    if (allowed.includes("/")) {
      return isIPInSubnet(ip, allowed);
    }
    // Если это обычный IP-адрес
    return allowed === ip;
  });
};

/**
 * Получает список запрещенных стран из переменных окружения
 * @returns массив кодов стран
 */
export const getBlockedCountries = (): string[] => {
  try {
    const blockedCountries = env.VITE_APP_BLOCKED_COUNTRIES;
    if (!blockedCountries) {
      // Значения по умолчанию, если переменная не задана
      return ["US", "GB", "UK", "SE"];
    }

    // Разбираем строку с разделителем-запятой
    return blockedCountries
      .split(",")
      .map((country) => country.trim().toUpperCase());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Ошибка при получении списка запрещенных стран:", error);
    return ["US", "GB", "UK", "SE"];
  }
};

/**
 * Получает список разрешенных IP-адресов и подсетей из переменных окружения
 * @returns массив IP-адресов и подсетей
 */
export const getAllowedIPs = (): string[] => {
  try {
    const allowedIPs = env.VITE_APP_ALLOWED_IPS;
    if (!allowedIPs) {
      // Значения по умолчанию, если переменная не задана
      return ["10.100.0.0/16"];
    }

    // Разбираем строку с разделителем-запятой
    return allowedIPs.split(",").map((ip) => ip.trim());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Ошибка при получении списка разрешенных IP-адресов:", error);
    return ["10.100.0.0/16"];
  }
};

/**
 * Проверяет, заблокирована ли страна пользователя
 * @param userCountry код страны пользователя
 * @param userIP IP-адрес пользователя
 * @returns true, если доступ заблокирован
 */
export const isCountryBlocked = (
  userCountry: string,
  userIP: string,
): boolean => {
  const blockedCountries = getBlockedCountries();
  const allowedIPs = getAllowedIPs();

  // Проверяем, находится ли страна в списке заблокированных
  const isBlockedCountry = blockedCountries.includes(userCountry.toUpperCase());

  // Если страна не заблокирована, доступ разрешен
  if (!isBlockedCountry) {
    return false;
  }

  // Для Нидерландов (NL) проверяем IP-адрес
  if (userCountry.toUpperCase() === "NL") {
    return !isIPAllowed(userIP, allowedIPs);
  }

  // Для остальных заблокированных стран доступ полностью запрещен
  return true;
};
